import { Component, EventEmitter, Inject, PLATFORM_ID, Output, OnInit } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { contactFormUrl, NameValidator, mailValidator, PhoneValidator } from '@app/helpers';
import { FormFieldsModule } from '@app/base-components';
import { DlContentClickService } from 'haba-base-components';
import { firstValueFrom } from 'rxjs';
import { HanaOndemandService, ResponseRequestService } from 'haba-emporix';
import { UserService } from '@app/services';
import { TranslatePipe, UserAddressService } from 'haba-core';
import { ProductAdvisorService } from '../product-advisor/product-advisor.service';
import { filter, first, switchMap } from 'rxjs/operators';
import { RouterLink } from '@angular/router';

interface Account {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  zipCode: string;
  customerNumber: string;
}

@Component({
  selector: 'app-contact-form',
  imports: [FormFieldsModule, CommonModule, TranslatePipe, RouterLink],
  templateUrl: './contact-form.component.html',
  styleUrl: './contact-form.component.scss',
})
export class ContactFormComponent implements OnInit {
  @Output() formSubmitted = new EventEmitter<UntypedFormGroup>();
  protected closed: boolean = false;

  readonly contactFormUrl = contactFormUrl;
  readonly emailUrl = 'mailto:beratung@wehrfritz.de';
  readonly callUrl = 'tel:+49 96 22 1';

  contactForm: UntypedFormGroup = this.fb.group({
    firstName: ['', [Validators.required, NameValidator()]],
    lastName: ['', [Validators.required, NameValidator()]],
    email: ['', [Validators.required, mailValidator()]],
    phone: ['', [Validators.required, PhoneValidator()]],
    zipCode: ['', [Validators.required]],
    customerNumber: [''],
  });

  constructor(
    @Inject(PLATFORM_ID) private platformId,
    protected fb: UntypedFormBuilder,
    private readonly hanaOndemandService: HanaOndemandService,
    private readonly productAdvisorService: ProductAdvisorService,
    private userService: UserService,
    private userAddressService: UserAddressService,
    private dlContentClickService: DlContentClickService
  ) {}

  ngOnInit() {
    this.userService
      .getCurrentUser()
      .pipe(
        filter((user) => Boolean(user)),
        first()
      )
      .subscribe((user) => {
        console.log('user', user);
        this.contactForm.get('firstName').setValue(user?.firstName ?? '');
        this.contactForm.get('lastName').setValue(user?.lastName ?? '');
        this.contactForm.get('email').setValue(user?.email ?? '');
        this.contactForm.get('phone').setValue(user?.phoneNumber ?? '');
        this.contactForm.get('customerNumber').setValue(user?.customerId ?? '');
      });

    this.userAddressService.loadAddresses();
    this.userAddressService
      .getAddressesLoading()
      .pipe(
        filter((loading) => !loading),
        switchMap(() => this.userAddressService.getAddresses()),
        filter((addresses) => !!addresses?.length),
        filter((addresses) => addresses.some((address) => address.defaultBillingAddress === true)),
        first()
      )
      .subscribe((addresses) => {
        this.contactForm.get('zipCode').setValue(addresses?.[0]?.postalCode ?? '');
      });
  }

  async submitForm(): Promise<void> {
    this.contactForm.markAllAsTouched();
    if (isPlatformBrowser(this.platformId) && this.contactForm.valid) {
      await this.createAccountAndTicket({
        firstname: this.contactForm.get('firstName').value,
        lastname: this.contactForm.get('lastName').value,
        email: this.contactForm.get('email').value,
        phone: this.contactForm.get('phone').value,
        zipCode: this.contactForm.get('zipCode').value,
        customerNumber: this.contactForm.get('customerNumber').value,
      });

      this.closed = true;
    }
  }

  async createAccountAndTicket(account: Account): Promise<ResponseRequestService> {
    const existingAccount = account?.customerNumber
      ? await firstValueFrom(
          this.hanaOndemandService.getAccount({
            AccountID: account.customerNumber,
          })
        )
      : null;

    const results = existingAccount?.data?.d?.results;
    const description = this.formatDescription(account);

    const ticket = await firstValueFrom(
      this.hanaOndemandService.createTicket({
        Name: 'Neue Anfrage vom Produktberater',
        BuyerPartyID: results?.length === 1 ? results?.[0]?.AccountID : '1000000000', // use dummy account if account not found
        ServiceSupportTeamPartyID: 'KAMGMT', // Kundenmanagement / customer management
        IncidentDescriptionText: description,
      })
    );

    return ticket;
  }

  trackClick(label: string, url: string): void {
    this.dlContentClickService.trackClick(label, undefined, url);
  }

  formatDescription(account: Account) {
    const header = [
      `Vorname: ${account.firstname}`,
      `Nachname: ${account.lastname}`,
      `E-Mail: ${account.email}`,
      `Telefonnummer: ${account.phone}`,
      `PLZ: ${account.zipCode}`,
      `Kundennummer: ${account.customerNumber}`,
    ];

    const separator = '-------------------------------';

    const messages = this.productAdvisorService.getChatHistory().flatMap((message) => {
      const lines = [separator, `${message.timestamp} - ${message.role}:`, message.content];

      if (message.recommendations) {
        lines.push(JSON.stringify(message.recommendations));
      }

      return lines;
    });

    return [...header, ...messages, separator].join('\n');
  }
}
